import { useEffect, useState } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge';
import { getFuncionariosPaginado } from '../../api/funcionarios';
import { getEmpresas } from '../../api/empresas';
import { useAuth } from '../../contexts/AuthContext';

import { labelCiclo } from '../../utils/ciclo';
import { BadgeConformidade } from '../../utils/conformidade';
import Paginacao from '../../components/common/Paginacao';

export default function Funcionarios() {
  const { isFenix } = useAuth();
  const [funcionarios, setFuncionarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscaInput, setBuscaInput] = useState('');
  const busca = useDebounce(buscaInput, 400);
  const [empresaFiltro, setEmpresaFiltro] = useState('');
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  // Reset página ao mudar busca debounced
  useEffect(() => { setSkip(0); }, [busca]); // eslint-disable-line

  useEffect(() => {
    setLoading(true);
    const params = { skip, limit };
    if (busca)         params.busca = busca;
    if (empresaFiltro) params.id_empresa = empresaFiltro;
    getFuncionariosPaginado(params)
      .then(({ items, total: t }) => { setFuncionarios(items); setTotal(t); })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, [skip, limit, busca, empresaFiltro]);

  useEffect(() => {
    if (isFenix) getEmpresas().then(setEmpresas);
  }, [isFenix]);

  const handleBusca = (v) => setBuscaInput(v);
  const handleEmpresa = (v) => { setEmpresaFiltro(v); setSkip(0); };

  const pagina = limit > 0 ? Math.floor(skip / limit) + 1 : 1;
  const totalPaginas = limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;
  const pagProps = {
    pagina,
    totalPaginas,
    inicio: total > 0 ? skip + 1 : 0,
    fim: Math.min(skip + limit, total),
    total,
    porPagina: limit,
    setPagina: (n) => setSkip((n - 1) * limit),
    setPorPagina: (n) => { setLimit(n); setSkip(0); },
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        title="Funcionários"
        subtitle="Perfis cadastrados e status dos últimos ciclos"
        action={
          <button className="btn-datafit" onClick={() => navigate('/funcionarios/novo')}>
            <i className="bi bi-person-plus me-2"></i>Novo funcionário
          </button>
        }
      />

      <div className="card-datafit">
        <div className="p-3 border-bottom d-flex gap-2 flex-wrap">
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 280 }}
            placeholder="Buscar por nome ou CPF..."
            value={buscaInput}
            onChange={(e) => handleBusca(e.target.value)}
          />
          {isFenix && (
            <select
              className="form-select"
              style={{ maxWidth: 260 }}
              value={empresaFiltro}
              onChange={(e) => handleEmpresa(e.target.value)}
            >
              <option value="">Todas as empresas</option>
              {empresas.map(e => (
                <option key={e.id_empresa} value={e.id_empresa}>
                  {e.nome_fantasia || e.razao_social}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="table-responsive">
          <table className="table table-datafit mb-0">
            <thead>
              <tr>
                <th>Funcionário</th>
                <th>Vínculo ativo</th>
                <th>Cargo / Local</th>
                <th>Conformidade</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {funcionarios.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted py-4">Nenhum funcionário encontrado</td></tr>
              ) : funcionarios.map(f => {
                const vinculoAtivo = f.vinculos?.find(v => v.ativo);
                return (
                  <tr key={f.id_funcionario}>
                    <td>
                      <div className="fw-500">{f.nome}</div>
                      <div className="small text-muted">CPF: {f.cpf}</div>
                    </td>
                    <td>
                      {vinculoAtivo ? (
                        <>
                          <StatusBadge status="ativo" />
                          <div className="small text-muted mt-1">{vinculoAtivo.empresa?.nome_fantasia || vinculoAtivo.empresa?.razao_social}</div>
                          <div className="small mt-1 d-flex gap-2 flex-wrap">
                            {vinculoAtivo.ultimo_ciclo_respiratorio && (
                              <span className="text-muted">
                                <i className="bi bi-lungs me-1"></i>{labelCiclo(vinculoAtivo.ultimo_ciclo_respiratorio)}
                              </span>
                            )}
                            {vinculoAtivo.ultimo_ciclo_auditivo && (
                              <span className="text-muted">
                                <i className="bi bi-ear me-1"></i>{labelCiclo(vinculoAtivo.ultimo_ciclo_auditivo)}
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <StatusBadge status="inativo" />
                      )}
                    </td>
                    <td>
                      {vinculoAtivo ? (
                        <>
                          <div>{vinculoAtivo.cargo}</div>
                          <div className="small text-muted">{vinculoAtivo.embarcacao}</div>
                        </>
                      ) : '—'}
                    </td>
                    <td>
                      <BadgeConformidade vinculo={vinculoAtivo} />
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-outline-secondary"
                          onClick={() => navigate(`/funcionarios/${f.id_funcionario}`)}>
                          Ver perfil
                        </button>
                        {isFenix && (
                          <button className="btn btn-sm btn-outline-primary"
                            title="Editar"
                            onClick={() => navigate(`/funcionarios/${f.id_funcionario}/editar`)}>
                            <i className="bi bi-pencil"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Paginacao {...pagProps} />
      </div>
    </>
  );
}
